/** @format */
import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoService } from '@ngneat/transloco';
import { ValidatablePlan } from 'common-typescript';
import { CourseUnit, CourseUnitAttainment } from 'common-typescript/types';
import * as _ from 'lodash-es';
import { Subject, Subscription, take } from 'rxjs';
import {
    ComponentDowngradeMappings,
    DEFAULT_PROMISE_HANDLER,
    DowngradedComponent,
    LocaleService,
    LocalizedStringPipe,
    ModalService,
    StaticMembers,
} from 'sis-common';
import { COURSE_UNIT_SERVICE } from '../../ajs-upgraded-modules';
import { AlertsService, AlertType } from '../../alerts/alerts-ng.service';
import { CourseUnitVersion } from '../../courseUnitInfoModal/course-unit-version-selector/course-unit-version-selector.component';
import { AppErrorHandler } from '../../error-handler/app-error-handler';
import { CourseUnitVersionService } from '../../service/course-unit-version.service';
import { PlanCourseUnitService } from '../../service/plan-course-unit.service';
import { PlanActionsService } from '../plan-actions-service/plan-actions.service';

export interface CourseUnitInfoModalValues {
    originalCourseUnit: CourseUnit;
    validatablePlan: ValidatablePlan;
    planActionsService: PlanActionsService;
    validatablePlanSubject: Subject<ValidatablePlan>;
}

@StaticMembers<DowngradedComponent>()
@Component({
    selector: 'sis-plan-course-unit-info-modal',
    templateUrl: './plan-course-unit-info-modal.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class PlanCourseUnitInfoModalComponent implements OnInit, OnDestroy {
    static downgrade: ComponentDowngradeMappings = {
        moduleName: 'sisPlancourseUnitInfoModal',
        directiveName: 'sisPlanCourseUnitInfoModal',
    };

    courseUnit: CourseUnit;
    courseUnitAttainment: CourseUnitAttainment;
    courseUnitVersionInPlan: CourseUnit;
    activeTab = 0;
    contentLanguage: string;
    allVersions: CourseUnit[] = [];
    fallbackName: string;
    displayNamesByCourseUnitId: { [id: string]: string } = {};
    belongsToCurrentCurriculumPeriod = true;
    locales: string[];
    courseUnitVersions: CourseUnitVersion[] = [];
    hasCoordinatingOrganisations = false;
    validatablePlanSubject: Subject<ValidatablePlan>;
    validatablePlanSubscription: Subscription;
    validatablePlan: ValidatablePlan;
    originalCourseUnit: CourseUnit;

    planActionsService: PlanActionsService;
    isCurrentVersionInPlan = false;
    isAnyVersionInPlan = false;
    courseUnitVersionInPlanIsSubstituted = false;
    canCurrentVersionBeSelectedInPlan = false;

    constructor(
        private appErrorHandler: AppErrorHandler,
        private localeService: LocaleService,
        private localizedStringPipe: LocalizedStringPipe,
        private planCourseUnitService: PlanCourseUnitService,
        private alertsService: AlertsService,
        private translocoService: TranslocoService,
        private courseUnitVersionService: CourseUnitVersionService,
        @Inject(COURSE_UNIT_SERVICE) private commonCourseUnitService: any,
        @Inject(ModalService.injectionToken) private values: CourseUnitInfoModalValues,
        @Inject(DEFAULT_PROMISE_HANDLER) private defaultPromiseHandler: any,
        public modal: NgbActiveModal,
    ) {
        this.courseUnit = values.originalCourseUnit;
        this.validatablePlan = values.validatablePlan;
        this.planActionsService = values.planActionsService;
        this.validatablePlanSubject = values.validatablePlanSubject;
        this.originalCourseUnit = _.cloneDeep(values.originalCourseUnit);
    }

    ngOnInit() {
        // all courseUnitInfoModal implementations use getSupportedLocales here,
        // but getOfficialLanguages would probably be more suitable
        this.locales = this.localeService.getSupportedLanguages();
        this.contentLanguage = this.localeService.getCurrentLanguage();

        this.fallbackName = this.localizedStringPipe.transform(this.courseUnit.name);

        this.commonCourseUnitService
            .findAllVersions(this.courseUnit.groupId)
            .then((allVersions: CourseUnit[]) => {
                this.allVersions = allVersions;
                this.commonCourseUnitService
                    .getVersionSelectorDataForCourseUnitVersions(this.allVersions)
                    .then((result: any) => {
                        this.courseUnitVersions = result.courseUnitVersions;
                        this.displayNamesByCourseUnitId = result.displayNamesByCourseUnitId;
                    })
                    .catch(this.defaultPromiseHandler.loggingRejectedPromiseHandler);
            })
            .catch(this.defaultPromiseHandler.loggingRejectedPromiseHandler);
        this.updateHasCoordinatingOrganisations();
        this.updateCourseUnitData();
        this.validatablePlanSubscription = this.validatablePlanSubject
            .pipe(this.appErrorHandler.defaultErrorHandler())
            .subscribe((validatablePlan: ValidatablePlan) => {
                this.validatablePlan = validatablePlan;
                this.updateCourseUnitData();
            });
    }

    updateCourseUnitData() {
        this.courseUnitAttainment = this.validatablePlan?.getCourseUnitAttainment(this.courseUnit?.id);
        this.isCurrentVersionInPlan = this.isCurrentCourseUnitVersionInPlan();
        this.isAnyVersionInPlan = this.isAnyCourseUnitVersionInPlan();
        this.courseUnitVersionInPlan = this.validatablePlan?.getCourseUnitInPlanByGroupId(this.courseUnit.groupId);
        this.courseUnitVersionInPlanIsSubstituted = this.validatablePlan?.isSubstituted(this.courseUnitVersionInPlan);
        this.planCourseUnitService
            .isVersionChangeAllowed(this.courseUnit, this.courseUnitVersionInPlan, this.validatablePlan)
            .then(result => {
                this.canCurrentVersionBeSelectedInPlan = result;
            });
    }

    ngOnDestroy() {
        this.validatablePlanSubscription?.unsubscribe();
    }

    setCourseUnitVersion(courseUnit: CourseUnit) {
        this.courseUnit = courseUnit;
        this.updateHasCoordinatingOrganisations();
        this.updateCourseUnitData();
    }

    changeCourseUnitVersionInPlan() {
        if (this.canCurrentVersionBeSelectedInPlan) {
            this.planActionsService?.changeCourseUnitVersion?.(this.courseUnitVersionInPlan, this.courseUnit);

            this.validatablePlanSubject
                .pipe(take(1), this.appErrorHandler.defaultErrorHandler())
                .subscribe((updatedValidatablePlan: ValidatablePlan) => {
                    if (
                        this.courseUnitVersionService.wasVersionChangeSuccessful(
                            this.originalCourseUnit,
                            this.courseUnit,
                            updatedValidatablePlan.plan,
                        )
                    ) {
                        this.alertsService.addAlert({
                            type: AlertType.SUCCESS,
                            message: this.translocoService.translate('STUDIES.COURSE_UNIT_INFO_MODAL.VERSION_CHANGE_SUCCESS_ALERT'),
                        });
                    }
                });
        }
    }

    isCurrentCourseUnitVersionInPlan(): boolean {
        const courseUnitInPlan = this.validatablePlan?.getCourseUnitInPlanByGroupId(this.courseUnit.groupId);
        if (!courseUnitInPlan || courseUnitInPlan.id !== this.courseUnit.id) {
            return false;
        }
        return true;
    }

    isAnyCourseUnitVersionInPlan() {
        const courseUnitInPlan = this.validatablePlan?.getCourseUnitInPlanByGroupId(this.courseUnit.groupId);
        if (!courseUnitInPlan) {
            return false;
        }
        return true;
    }

    openTab(tabIndex: number) {
        this.activeTab = tabIndex;
    }

    dismiss() {
        this.modal.dismiss();
    }

    updateHasCoordinatingOrganisations() {
        this.hasCoordinatingOrganisations = _.some(this.courseUnit.organisations, {
            roleUrn: 'urn:code:organisation-role:coordinating-organisation',
        });
    }
}
